import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Spinner,
  useMediaQuery,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';

import {
  Footer,
  Header,
  ImageComponent,
  PageWrapper,
  Spacer,
  Text,
} from '../../components';
import { PageAlignment } from '../../components/PageWrapper';
import { useHeader } from '../../contexts/useHeader';
import { getProjects, projectsLinks } from '../../data/mock.data.projects';
import { Project } from '../../models/Project';
import { TextType } from '../../models/Text';
import ErrorPage404 from '../404';

import ImageLightboxModel from './ImageLightboxModel';


const richTextSx = {
  fontFamily: 'inherit',
  color: 'gray.500',
  fontWeight: 300,
  letterSpacing: '1.5px',
  lineHeight: 'normal',

  // Set base font size on the container so all HTML variants match
  fontSize: { base: '12px', md: '14px' },

  '& *': {
    fontFamily: 'inherit',
    color: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
  },

  // Handle common WYSIWYG wrappers
  '& div, & span': {
    margin: 0,
    padding: 0,
  },

  '& p': {
    margin: 0,
  },

  '& p + p': {
    marginTop: '12px',
  },

  '& ul, & ol': {
    paddingLeft: '18px',
    margin: 0,
    marginTop: '12px',
    marginBottom: '12px',
  },

  '& li': {
    marginBottom: '8px',
  },

  '& strong, & b': {
    fontWeight: 700,
  },

  '& em, & i': {
    fontStyle: 'italic',
  },

  '& a': {
    textDecoration: 'none',
    fontWeight: 700,
  },

  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontWeight: 700,
    margin: 0,
    marginTop: '14px',
    marginBottom: '10px',
  },
};

const ProjectDetailsPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobile] = useMediaQuery('(max-width: 767px)');
  const { headerDimensions } = useHeader();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [activeImageSrc, setActiveImageSrc] = useState<string>('');
  const [activeImageAlt, setActiveImageAlt] = useState<string>('');

  useEffect(() => {
    getProjects()
      .then((items: Project[]) => setProjects(items))
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load projects from Cockpit:', err);
        setProjects([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const project = useMemo<Project | null>(() => {
    if (id == null) return null;

    const numericId = parseInt(id, 10);
    if (Number.isNaN(numericId)) return null;

    return projects.find((p: Project) => p.id === numericId) || null;
  }, [id, projects]);

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  const handleSeeDetails = () => {
    const element = document.getElementById('projectDetails');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleClickButton = useCallback(() => {
    navigate('/projects');
  }, [navigate]);

  const openLightbox = useCallback((src: string, alt: string) => {
    setActiveImageSrc(src);
    setActiveImageAlt(alt);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setActiveImageSrc('');
    setActiveImageAlt('');
  }, []);

  const renderDetail = (label: string, value: string) => (
    <Flex flexDirection="column" alignItems="center" justifyContent="center">
      <Text type={{ base: TextType.Inter12Bold, md: TextType.Inter14Bold }}>
        {label.toUpperCase()}
      </Text>
      <Spacer mobileSize={6} size={12} />
      <Text type={TextType.Inter12Light}>{value}</Text>
      <Spacer mobileSize={30} size={0} />
    </Flex>
  );

  const renderSection = (
    title: string,
    descriptionHtml: string,
    image?: string,
    reverse: boolean = false
  ) => {
    return (
      <Flex
        justifyContent="space-between"
        alignItems="center"
        flexDirection={{
          base: 'column-reverse',
          md: reverse ? 'row-reverse' : 'row',
        }}
      >
        <Flex
          flex={1}
          height="100%"
          width={{ base: '100%', md: 'auto' }}
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="space-between"
          marginRight={{ base: 0, md: reverse ? 0 : '60px' }}
          marginLeft={{ base: 0, md: reverse ? '60px' : 0 }}
          paddingX={{ base: '20px', md: 0 }}
        >
          <Text
            type={{ base: TextType.Inter12Bold, md: TextType.Inter14Bold }}
            others={{
              marginTop: { base: '20px', md: 0 },
            }}
          >
            {title.toUpperCase()}
          </Text>

          <Spacer mobileSize={10} size={20} />

          <Box
            sx={{
              ...richTextSx,
              textAlign: 'justify',
            }}
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        </Flex>

        <Box
  width={{ base: '100%', md: '60%' }}
  flexShrink={0}
>
  <ImageComponent
    altMessage="Project concept"
    src={image}
    others={{
      width: '100%',
      height: 'auto',
      objectFit: 'contain',
      display: 'block',
    }}
  />
</Box>
      </Flex>
    );
  };

  if (loading) {
    return (
      <PageWrapper>
        <Flex width="100%" justifyContent="center">
          <Spinner />
        </Flex>
      </PageWrapper>
    );
  }

  if (project == null) {
  return <ErrorPage404 />;
  }

  const collabs = Array.isArray(project.collabs) ? project.collabs : [];
  const collabsText =
    collabs.length > 0 ? collabs.map((c) => c.toUpperCase()).join(', ') : '';

  return (
    <>
      <Header links={projectsLinks} />

      <PageWrapper pageAlignment={PageAlignment.END} bgUri={project.heroImage}>
        <Text
          type={TextType.Inter18Bold}
          color="white"
          others={{
            marginBottom: { base: '10px', md: '40px' },
          }}
        >
          {project.title.toUpperCase()}
        </Text>

        {isMobile ? (
          <IconButton
            variant="link"
            aria-label="Scroll down to see the content"
            icon={<ChevronDownIcon boxSize={50} />}
            color="white"
            size="md"
            marginBottom="10px"
            onClick={handleSeeDetails}
          />
        ) : null}
      </PageWrapper>

      <PageWrapper pageAlignment={PageAlignment.START} id="projectDetails">
        <Box width={{ base: '100%', md: '80%' }} marginTop="20px">
          <Spacer size={headerDimensions?.height} />

          <Flex
            width="100%"
            flexDirection={{ base: 'column', md: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            paddingX={{ base: '20px', md: 0 }}
          >
            {renderDetail(t('projectDetails.category'), project.category)}
            {renderDetail(t('projectDetails.client'), project.client)}
            {renderDetail(t('projectDetails.location'), project.location.name)}
            {renderDetail(t('projectDetails.year'), project.year)}
            {collabsText
              ? renderDetail(t('projectDetails.collab'), collabsText)
              : null}
          </Flex>

          <Spacer mobileSize={30} size={60} />

          {project.concept
            ? renderSection(
                t('projectDetails.conceptSection'),
                project.concept.description,
                project.concept.image
              )
            : null}

          <Spacer mobileSize={40} size={60} />

          {project.process
            ? renderSection(
                t('projectDetails.processSection'),
                project.process.description,
                project.process.image,
                true
              )
            : null}
        </Box>

        <Spacer mobileSize={60} size={headerDimensions?.height} />

        {project.images && project.images.length > 0 ? (
          <Grid
  width="100%"
  templateColumns={{
    base: 'repeat(1, 1fr)',
    md: 'repeat(2, 1fr)',
    lg: 'repeat(3, 1fr)',
  }}
  columnGap="12px"
  rowGap="12px"
  alignContent="start"
>
            {project.images.map((image: string, index: number) => (
              <GridItem
                key={`${project.title}-image-${index}`}
                w="100%"
                aspectRatio={1}
              >
                <button
                  type="button"
                  onClick={() =>
                    openLightbox(image, `${project.title} image ${index + 1}`)
                  }
                  style={{
                    width: '100%',
                    height: '100%',
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    bgImage={image}
                    width="100%"
                    height="100%"
                    backgroundSize="cover"
                    backgroundPosition="center"
                  />
                </button>
              </GridItem>
            ))}
          </Grid>
        ) : null}

        <Spacer mobileSize={30} size={40} />

        <Button
          variant="solid"
          bgColor="primary"
          marginTop={{ base: 0, md: '30px' }}
          width={{ base: '50%', md: '33.3333%' }}
          borderRadius={0}
          onClick={handleClickButton}
        >
          <Text
            type={{ base: TextType.Inter12Bold, md: TextType.Inter14Bold }}
            color="white"
          >
            {t('projectDetails.backButton').toUpperCase()}
          </Text>
        </Button>

        <Spacer mobileSize={30} size={40} />
        <Footer />
      </PageWrapper>

      {lightboxOpen && activeImageSrc ? (
        <ImageLightboxModel
          isOpen={lightboxOpen}
          onClose={closeLightbox}
          src={activeImageSrc}
          alt={activeImageAlt || 'Project image'}
        />
      ) : null}
    </>
  );
};

export default ProjectDetailsPage;